import { parseISO, subDays } from "date-fns";
import { lisbonDateParts } from "../../../dashboard-framework/businessMinutes";
import { DateRange } from "../../../dashboard-framework/types";
import { ClientTypeKey, clientTypeOfAge, coarseDisorderGroupOf, CoarseDisorderGroupKey } from "../shared/disorderGroups";

// Rollups puros da conversão em cliente. Universo = consultas de avaliação (service 37) realizadas.
// Relatórios = nº de consultas; Iniciaram = tratamentos DISTINTOS com date_of_start; % = Iniciaram/Relatórios.
// Adulto/Criança calculado da idade na DATA DA CONSULTA (clients.birthday) — o único campo disponível
// no denominador (age_when_started só existe depois do tratamento começar). Ver spec.md.

export const NO_TECHNICIAN_KEY = "__no_technician__";
export const NO_TECHNICIAN_LABEL = "Sem atribuição";

// Sufixos das contagens por ponto no tooltip do gráfico (fração iniciaram/relatórios).
export const CONVERSION_COUNT_SUFFIX = "_rel";
export const CONVERSION_NUM_SUFFIX = "_ini";

type ConversionPersonUser = { id: string; first_name: string | null; last_name: string | null };

export type ConversionSessionItem = {
    id: number | string;
    start_date: string | null;
    technician?: { id: number | string; user_id?: ConversionPersonUser | null } | null;
    treatment?: {
        id: number | string;
        date_of_start: string | null;
        treatment_status_new: string | null;
        diagnose_main?: { id: number | string; name?: string | null } | null;
        client?: { id: number | string; client_id: string | null; birthday: string | null } | null;
    } | null;
};

export type ConversionFilters = { groupKey: CoarseDisorderGroupKey | null; clientType: ClientTypeKey | null };

export type ConversionSummary = { relatorios: number; iniciaram: number; pct: number | null };

export type ConversionRateBand = "low" | "mid" | "high";

export type ConversionMonthlyGroup = { key: string; items: Array<ConversionSessionItem> };

// Dia civil (Europe/Lisbon) da consulta (timestamptz) em yyyy-MM-dd.
export const consultaDayKeyOf = (startDate: string | null) => {
    if (!startDate) return null;
    const parts = lisbonDateParts(startDate);
    const dayKey = `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
    return dayKey;
};

const lisbonMonthIndexOf = (startDate: string) => {
    const parts = lisbonDateParts(startDate);
    const monthIndex = parts.year * 12 + (parts.month - 1);
    return monthIndex;
};

const monthIndexOfDayString = (day: string) => {
    const year = Number(day.slice(0, 4));
    const month = Number(day.slice(5, 7));
    const monthIndex = year * 12 + (month - 1);
    return monthIndex;
};

export const ageOnConsulta = (birthday: string | null | undefined, consultaStartDate: string | null) => {
    if (!birthday || !consultaStartDate) return null;
    const birthYear = Number(birthday.slice(0, 4));
    const birthMonth = Number(birthday.slice(5, 7));
    const birthDay = Number(birthday.slice(8, 10));
    const consulta = lisbonDateParts(consultaStartDate);
    let age = consulta.year - birthYear;
    const beforeBirthday = consulta.month < birthMonth || (consulta.month === birthMonth && consulta.day < birthDay);
    if (beforeBirthday) age -= 1;
    return age;
};

export const conversionClientTypeOf = (item: ConversionSessionItem) => {
    const age = ageOnConsulta(item.treatment?.client?.birthday ?? null, item.start_date);
    const clientType = clientTypeOfAge(age);
    return clientType;
};

export const conversionPersonKeyOf = (item: ConversionSessionItem) => {
    const userId = item.technician?.user_id?.id;
    const personKey = userId ? String(userId) : NO_TECHNICIAN_KEY;
    return personKey;
};

export const conversionPersonNameOf = (item: ConversionSessionItem) => {
    const user = item.technician?.user_id;
    if (!user) return NO_TECHNICIAN_LABEL;
    const composed = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
    const personName = composed.length > 0 ? composed : NO_TECHNICIAN_LABEL;
    return personName;
};

export const isStartBeforeConsulta = (item: ConversionSessionItem) => {
    const dateOfStart = item.treatment?.date_of_start;
    if (!dateOfStart) return false;
    const consultaDay = consultaDayKeyOf(item.start_date);
    if (!consultaDay) return false;
    const startDay = dateOfStart.slice(0, 10);
    const before = startDay < consultaDay;
    return before;
};

export const anomalyTreatmentCount = (items: Array<ConversionSessionItem>) => {
    const treatmentIds = new Set<string>();
    items.forEach((item) => {
        if (isStartBeforeConsulta(item) && item.treatment) treatmentIds.add(String(item.treatment.id));
    });
    return treatmentIds.size;
};

export const matchesConversionFilters = (item: ConversionSessionItem, filters: ConversionFilters) => {
    if (filters.groupKey !== null && coarseDisorderGroupOf(item.treatment?.diagnose_main?.id) !== filters.groupKey) return false;
    if (filters.clientType !== null && conversionClientTypeOf(item) !== filters.clientType) return false;
    return true;
};

export const filterConversionItems = (items: Array<ConversionSessionItem>, filters: ConversionFilters) => {
    const filtered = items.filter((item) => matchesConversionFilters(item, filters));
    return filtered;
};

const distinctStartedTreatmentCount = (items: Array<ConversionSessionItem>) => {
    const treatmentIds = new Set<string>();
    items.forEach((item) => {
        if (item.treatment && item.treatment.date_of_start) treatmentIds.add(String(item.treatment.id));
    });
    return treatmentIds.size;
};

export const conversionSummaryOf = (items: Array<ConversionSessionItem>) => {
    const relatorios = items.length;
    const iniciaram = distinctStartedTreatmentCount(items);
    const pct = relatorios === 0 ? null : (iniciaram / relatorios) * 100;
    const summary: ConversionSummary = { relatorios, iniciaram, pct };
    return summary;
};

export const scopedConversionSummary = (items: Array<ConversionSessionItem>, clientType: ClientTypeKey) => {
    const scoped = items.filter((item) => conversionClientTypeOf(item) === clientType);
    const summary = conversionSummaryOf(scoped);
    return summary;
};

export type ConversionBreakdown = { total: ConversionSummary; adults: ConversionSummary; children: ConversionSummary };

// Repartição de um segmento (grupo de perturbação, ou null = todos) em total + adultos + crianças.
// Alimenta os cartões de topo (Total/Adultos/Crianças + um por grupo com chips adulto/criança).
export const conversionGroupBreakdown = (items: Array<ConversionSessionItem>, groupKey: CoarseDisorderGroupKey | null) => {
    const groupItems = groupKey === null ? items : items.filter((item) => coarseDisorderGroupOf(item.treatment?.diagnose_main?.id) === groupKey);
    const breakdown: ConversionBreakdown = {
        total: conversionSummaryOf(groupItems),
        adults: scopedConversionSummary(groupItems, "adults"),
        children: scopedConversionSummary(groupItems, "children"),
    };
    return breakdown;
};

export const conversionRateBandOf = (pct: number | null) => {
    if (pct === null) return null;
    if (pct < 30) return "low" as ConversionRateBand;
    if (pct <= 35) return "mid" as ConversionRateBand;
    return "high" as ConversionRateBand;
};

export const monthLabelFromMonthIndex = (monthIndex: number) => {
    const year = Math.floor(monthIndex / 12);
    const month = (monthIndex % 12) + 1;
    const label = `${String(month).padStart(2, "0")}/${year}`;
    return label;
};

// Meses contíguos a mostrar nos gráficos: do range (mês do gte ao mês do dia anterior a lt, exclusivo)
// ou, sem range (sem_periodo), do mês da consulta mais antiga até ao mês corrente (Lisboa).
export const conversionMonthIndexes = (range: DateRange | null, items: Array<ConversionSessionItem>, now: Date) => {
    let startIndex: number;
    let endIndex: number;
    if (range) {
        startIndex = monthIndexOfDayString(range.gte);
        const lastIncludedDay = subDays(parseISO(range.lt), 1);
        endIndex = lastIncludedDay.getFullYear() * 12 + lastIncludedDay.getMonth();
    } else {
        const itemMonthIndexes = items.filter((item) => item.start_date).map((item) => lisbonMonthIndexOf(item.start_date as string));
        if (itemMonthIndexes.length === 0) return [];
        startIndex = Math.min(...itemMonthIndexes);
        endIndex = lisbonMonthIndexOf(now.toISOString());
    }
    if (endIndex < startIndex) return [];
    const monthIndexes: Array<number> = [];
    for (let monthIndex = startIndex; monthIndex <= endIndex; monthIndex++) monthIndexes.push(monthIndex);
    return monthIndexes;
};

export const buildConversionMonthlyPoints = (monthIndexes: Array<number>, groups: Array<ConversionMonthlyGroup>) => {
    const points = monthIndexes.map((monthIndex) => {
        const point: Record<string, string | number> = { monthLabel: monthLabelFromMonthIndex(monthIndex) };
        groups.forEach((group) => {
            const monthItems = group.items.filter((item) => item.start_date && lisbonMonthIndexOf(item.start_date) === monthIndex);
            const summary = conversionSummaryOf(monthItems);
            if (summary.pct === null) return; // mês sem consultas → key omitida (gap na linha)
            point[group.key] = summary.pct;
            point[`${group.key}${CONVERSION_COUNT_SUFFIX}`] = summary.relatorios;
            point[`${group.key}${CONVERSION_NUM_SUFFIX}`] = summary.iniciaram;
        });
        return point;
    });
    return points;
};

// Grupos por pessoa para as LINHAS do gráfico por psicóloga: exclui "Sem atribuição" (o Zoho não
// desenha essa linha) e ordena por volume de consultas desc (cor estável por índice no componente).
export const conversionChartPersonGroups = (items: Array<ConversionSessionItem>) => {
    const byPersonKey = new Map<string, { key: string; name: string; items: Array<ConversionSessionItem> }>();
    items.forEach((item) => {
        const personKey = conversionPersonKeyOf(item);
        if (personKey === NO_TECHNICIAN_KEY) return;
        const group = byPersonKey.get(personKey) || { key: personKey, name: conversionPersonNameOf(item), items: [] };
        group.items.push(item);
        byPersonKey.set(personKey, group);
    });
    const groups = Array.from(byPersonKey.values()).sort((first, second) => second.items.length - first.items.length || first.name.localeCompare(second.name));
    return groups;
};
